import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { FileDown, Printer, Hourglass, HelpCircle } from "lucide-react";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { Button, Card, Field, Input, Select, Spinner } from "../../components/ui-kit";
import { LABEL_FORMATS } from "../../lib/labelFormats";
import { formatItalianDate } from "../../lib/dateUtils";
import BlastTrace, { blastDate, blastType } from "../../components/BlastTrace";
import { ShelfLifeResultBadge, ShelfLifeWhyDrawer } from "../../components/ShelfLifeWhyDrawer";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { isIOS } from "../../lib/platform";
import { openServerPdfFile, pdfFileName, preparePdfWindow } from "../../lib/pdfExport";

export default function EtichetteAbbattuti() {
  const [params, setParams] = useSearchParams();
  const recordId = params.get("record") || "";
  const [month, setMonth] = useState(() => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" }).slice(0, 7));
  const [records, setRecords] = useState(null);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const labelRef = useRef(null);
  const [formatKey, setFormatKey] = useState("62x100");
  const [printError, setPrintError] = useState("");
  const [sleAssessment, setSleAssessment] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const format = LABEL_FORMATS.find((item) => item.key === formatKey);

  useEffect(() => {
    let active = true;
    setRecords(null); setListError("");
    api.get("/app/blast-chilling", { params: { month } }).then(({ data }) => {
      if (active) setRecords(data.records || []);
    }).catch((err) => { if (active) { setRecords([]); setListError(formatApiErrorDetail(err.response?.data?.detail)); } });
    return () => { active = false; };
  }, [month]);

  useEffect(() => {
    let active = true;
    setRecord(null); setError(""); setPrintError(""); setSleAssessment(null);
    if (recordId) api.get(`/app/blast-chilling/${encodeURIComponent(recordId)}`).then(({ data }) => {
      if (active) setRecord(data.record);
      if (active && data.record?.production_id) {
        api.get("/app/shelf-life/assessments", { params: { production_id: data.record.production_id } })
          .then(({ data: sle }) => { if (active) setSleAssessment(sle.assessments?.[0] || null); })
          .catch(() => { if (active) setSleAssessment(null); });
      }
    }).catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); });
    return () => { active = false; };
  }, [recordId]);

  const selected = record?.id === recordId ? record : null;
  // "Crea PDF e stampa": lo stesso PDF del flusso iOS su qualunque dispositivo
  // (dal telefono è più affidabile della stampa diretta della pagina).
  const createPdf = async () => {
    const label = labelRef.current;
    if (!selected?.public_path || !label) return;
    if (label.scrollHeight > label.clientHeight || label.scrollWidth > label.clientWidth) {
      setPrintError("Il testo supera il formato selezionato. Scegli un formato più grande per stampare tutti i riferimenti.");
      return;
    }
    setPrintError("");
    const win = preparePdfWindow();
    try {
      await openServerPdfFile(`/app/blast-chilling/${encodeURIComponent(selected.id)}/label.pdf`, { params: { format: formatKey }, fileName: pdfFileName("etichetta-abbattimento", selected.product_name, selected.production_lot), win });
    } catch {
      setPrintError("Impossibile creare il PDF dell'etichetta. Riprova.");
    }
  };
  const print = () => {
    const label = labelRef.current;
    if (!selected?.public_path || !label) return;
    if (label.scrollHeight > label.clientHeight || label.scrollWidth > label.clientWidth) {
      setPrintError("Il testo supera il formato selezionato. Scegli un formato più grande per stampare tutti i riferimenti.");
      return;
    }
    setPrintError("");
    if (!isIOS()) {
      // Android/desktop: window.print() diretto, più affidabile del PDF qui perché il
      // gestore PDF su Android varia troppo da dispositivo a dispositivo (a volte lo apre
      // un visualizzatore che non rispetta il formato pagina).
      setPrintPageSize(format.widthMm, format.heightMm);
      window.print();
      clearPrintPageSize();
      return;
    }
    // iOS: PDF vero invece di window.print(). Safari aggiunge sempre un header/footer
    // (URL, data, numero pagina) quando si stampa una PAGINA WEB, sovrapponendosi
    // all'etichetta e spingendola su un secondo foglio fantasma. Stampando un PDF
    // vero da AirPrint questo non succede (vale solo per le pagine web).
    openServerPdf(`/app/blast-chilling/${encodeURIComponent(selected.id)}/label.pdf`, { format: formatKey });
  };

  return <div className="space-y-6" data-testid="blast-labels-page">
    <div className="no-print"><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Stampa QR code</p><div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Etichette prodotti abbattuti</h1><AcademyTitleBadge /></div><p className="mt-2 text-sm text-slate-600">Un’etichetta e un QR per ogni abbattimento, collegati al lotto della produzione.</p></div>
    <Card className="no-print space-y-4 p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Mese dell’abbattimento"><Input type="month" value={month} onChange={(event) => { if (event.target.value) { setMonth(event.target.value); setParams({}); } }} data-testid="blast-label-month" /></Field>
        <Field label="Abbattimento da etichettare"><Select value={recordId} onChange={(event) => setParams(event.target.value ? { record: event.target.value } : {})} data-testid="blast-label-record">
          <option value="">Seleziona un abbattimento</option>
          {recordId && !records?.some((item) => item.id === recordId) && <option value={recordId}>{selected ? `${selected.product_name} — ${blastDate(selected.started_at)}` : "Abbattimento selezionato"}</option>}
          {(records || []).map((item) => <option key={item.id} value={item.id}>{item.product_name} — {item.production_lot} — {blastDate(item.started_at)} — {item.status === "conforme" ? "Conforme" : "NON CONFORME"} — {item.id}</option>)}
        </Select></Field>
      </div>
      <Field label="Formato etichetta"><Select value={formatKey} onChange={(event) => { setFormatKey(event.target.value); setPrintError(""); }} data-testid="blast-label-format">{LABEL_FORMATS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</Select></Field>
      {records === null && <Spinner />}
      {listError && <p role="alert" className="text-red-700">{listError}</p>}
      {!listError && records?.length === 0 && <p className="text-sm text-slate-600">Nessun abbattimento nel mese selezionato. <Link className="underline" to="/app/abbattimento">Registra un abbattimento</Link>.</p>}
      {format.small && <p className="text-sm text-slate-600">Nei formati da 29 mm l’etichetta mostra prodotto, lotto, fine processo, temperature, durata, esito e riferimento. Il nome completo e tutti i dettagli sono disponibili nel QR.</p>}
      <p className="text-sm text-slate-600">Formato {format.label}. Stampa al 100% senza intestazioni e piè di pagina. Il QR apre la scheda pubblica dell’abbattimento e la tracciabilità della produzione.</p>
    </Card>
    {error && <p role="alert" className="no-print text-red-700">{error}</p>}
    {printError && <p role="alert" className="no-print text-red-700">{printError}</p>}
    {recordId && !selected && !error && <Spinner />}
    {selected && <>
      {sleAssessment && (
        <Card className="p-4 no-print border-navy/20" data-testid="blast-shelf-life-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-navy text-gold"><Hourglass className="h-4.5 w-4.5" /></div>
              <div>
                <div className="text-sm font-semibold text-navy">Shelf life calcolata dal DF Food Safety Expert Engine: {sleAssessment.result.shelf_life_days} giorni</div>
                <div className="text-xs text-slate-500">Sulla temperatura reale di fine abbattimento ({sleAssessment.input.temp_c}°C). Usata per l'etichetta della produzione.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShelfLifeResultBadge needsValidation={sleAssessment.result.needs_validation} />
              <Button variant="outline" onClick={() => setShowWhy(true)}><HelpCircle className="w-4 h-4" /> Perché?</Button>
            </div>
          </div>
        </Card>
      )}
      <ShelfLifeWhyDrawer assessment={showWhy ? sleAssessment : null} onClose={() => setShowWhy(false)} />
      <div className="no-print flex flex-wrap gap-3">
        <Button onClick={print} disabled={!selected.public_path}><Printer className="h-4 w-4" /> Stampa etichetta</Button>
        <Button variant="outline" onClick={createPdf} disabled={!selected.public_path} data-testid="blast-label-pdf"><FileDown className="h-4 w-4" /> Crea PDF e stampa</Button>
        {selected.public_path && <Link className="inline-flex min-h-[44px] items-center text-sky-800 underline" to={selected.public_path} target="_blank" rel="noopener noreferrer">Verifica QR e scheda pubblica</Link>}
        <Link className="inline-flex min-h-[44px] items-center text-sky-800 underline" to={`/app/etichette?prod=${encodeURIComponent(selected.production_id)}`}>Etichetta della produzione</Link>
      </div>
      {!selected.public_path && <p role="alert" className="no-print text-red-700">QR non disponibile: la produzione collegata non è disponibile.</p>}
      <div className="blast-label-preview overflow-x-auto">
        <BlastProductLabel record={selected} format={format} labelRef={labelRef} sleAssessment={sleAssessment} />
      </div>
      {/* Copia dedicata al flusso window.print() (Android/desktop), portalata in document.body
          per lo stesso motivo dell'etichetta di produzione in Etichette.jsx. Su iOS resta
          inutilizzata (si stampa il PDF generato dal backend). */}
      {selected.public_path && createPortal(<BlastProductLabel record={selected} format={format} sleAssessment={sleAssessment} printable />, document.body)}
      <div className="no-print"><BlastTrace records={[selected]} internal /></div>
    </>}
  </div>;
}

function BlastProductLabel({ record, format, labelRef, sleAssessment, printable = false }) {
  const effectiveDays = sleAssessment?.result?.shelf_life_days ?? record.production_shelf_life_days;
  const producedAt = record.production_created_at ? new Date(record.production_created_at) : null;
  const expiryDate = (effectiveDays != null && producedAt) ? new Date(producedAt.getTime() + effectiveDays * 86400000) : null;
  return (
    <div ref={labelRef}
      className={`blast-product-label${format.small ? " blast-label-compact" : ""}${printable ? " print-area" : ""}`}
      data-format={format.key}
      style={{ "--blast-width": `${format.widthMm}mm`, "--blast-height": `${format.heightMm}mm`, "--blast-qr": `${format.qrMm}mm` }}
      data-testid={printable ? "blast-label-print" : "blast-label-preview"}>
      <div className="blast-label-main">
        <div className="blast-label-copy">
          <p className="blast-label-heading">{format.small ? `ABBATTUTO · ${{ parasite_freezing: "ANTIPAR.", blast_freezing: "SURGEL." }[record.process_type] || "RAFFR."}` : "PRODOTTO ABBATTUTO"}</p>
          <h2 title={record.product_name}>{record.product_name}</h2>
          <p><strong>Lotto:</strong> {record.production_lot}</p>
          {!format.small && <><p>{blastType(record)}</p><p><strong>Inizio:</strong> {blastDate(record.started_at)}</p></>}
          <p><strong>Fine:</strong> {blastDate(record.completed_at)}</p>
          <p>{record.initial_core_temp} °C → {record.final_core_temp} °C · {record.duration_minutes} min</p>
          <p><strong>Esito: {record.status === "conforme" ? "CONFORME" : "NON CONFORME"}</strong></p>
          {expiryDate && (
            <p><strong>{format.small ? "Scad." : "Da consumarsi entro"}:</strong> {formatItalianDate(expiryDate)}{!format.small && effectiveDays != null && ` (${effectiveDays} gg)`}</p>
          )}
        </div>
        {record.public_path && <div className="blast-label-qr"><QRCodeSVG value={`${window.location.origin}${record.public_path}`} size={Math.round(format.qrMm * 96 / 25.4)} marginSize={4} level="M" title={`QR abbattimento ${record.label_reference}`} />{!format.small && <p>Scheda abbattimento<br />e tracciabilità</p>}</div>}
      </div>
      <p className="blast-label-reference">Rif. etichetta: {record.label_reference}</p>
    </div>
  );
}
